import React, { useState, useEffect } from "react";
import { ArrowUpRight, ArrowLeft, Search, Filter } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const RentalSkeleton = () => {
  return (
    <div className="animate-pulse min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 md:px-8 lg:px-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="h-8 w-48 bg-gray-300 rounded-md"></div>
          <div className="h-4 w-32 bg-gray-300 rounded-md"></div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="h-10 w-full bg-gray-300 rounded-md"></div>
            <div className="h-10 w-full bg-gray-300 rounded-md"></div>
            <div className="h-10 w-full bg-gray-300 rounded-md"></div>
            <div className="h-10 w-full bg-gray-300 rounded-md"></div>
          </div>
        </div>

        {/* Results Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {Array.from({ length: 8 }).map((_, idx) => (
            <div
              key={idx}
              className="bg-white rounded-lg shadow-md p-4 space-y-4"
            >
              <div className="h-40 bg-gray-300 rounded-md"></div>
              <div className="h-5 w-3/4 bg-gray-300 rounded-md"></div>
              <div className="h-4 w-1/2 bg-gray-300 rounded-md"></div>
              <div className="h-4 w-1/3 bg-gray-300 rounded-md"></div>
              <div className="h-8 w-full bg-gray-300 rounded-md"></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const AllRentalFields = () => {
  const [rentals, setRentals] = useState([]);
  const [filteredRentals, setFilteredRentals] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [priceFilter, setPriceFilter] = useState("all");
  const [timeFilter, setTimeFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const allRentals = () => {
    setLoading(true);
    let data = "";

    let config = {
      method: "get",
      maxBodyLength: Infinity,
      url: `${process.env.REACT_APP_API_URL}/fields-rental/all-fields`,
      headers: {},
      data: data,
    };

    axios
      .request(config)
      .then((response) => {
        console.log(JSON.stringify(response.data));
        const rentalData = response?.data?.rentals || [];
        setRentals(rentalData);
        setFilteredRentals(rentalData);
        setLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
      });
  };

  useEffect(() => {
    allRentals();
  }, []);

  // Filter and search functionality
  useEffect(() => {
    let filtered = rentals;

    // Search filter
    if (searchQuery.trim()) {
      filtered = filtered.filter((rental) =>
        rental.fieldName.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Price filter
    if (priceFilter !== "all") {
      if (priceFilter === "low") {
        filtered = filtered.filter((rental) => rental.price <= 250);
      } else if (priceFilter === "medium") {
        filtered = filtered.filter(
          (rental) => rental.price > 250 && rental.price <= 350
        );
      } else if (priceFilter === "high") {
        filtered = filtered.filter((rental) => rental.price > 350);
      }
    }

    // Time filter
    if (timeFilter !== "all") {
      filtered = filtered.filter((rental) => rental.time === timeFilter);
    }

    setFilteredRentals(filtered);
  }, [searchQuery, priceFilter, timeFilter, rentals]);

  const handleBackClick = () => {
    navigate(-1);
  };

  const RentalCard = ({ rental }) => (
    <div
      className="group relative overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-1 bg-black h-80 cursor-pointer"
      onClick={() => {
        navigate("/rental", {
          state: {
            selectedField: rental,
            shouldScrollToCalendar: true,
          },
        });
      }}
    >
      <div className="relative w-full h-full overflow-hidden">
        <img
          src={rental?.imageUrl}
          alt={rental?.fieldName}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 opacity-90"
          onError={(e) => {
            e.target.src =
              "https://via.placeholder.com/400x300/333/fff?text=Field+Image";
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />

        {/* Field Name */}
        <div className="absolute min-h-12 bottom-2 left-4 right-4 z-10">
          <h3 className="text-white font-semibold font-heading text-sm sm:text-base tracking-wide uppercase leading-tight line-clamp-2">
            {rental?.fieldName}
          </h3>
        </div>

        {/* Hover Overlay */}
        {/* <div className="absolute p-6 inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-all duration-300 flex flex-col items-center justify-between">
          <div className="flex items-center w-full">
            <span className="px-3 py-1 text-[#FF4141] bg-white text-xs sm:text-sm font-heading font-semibold rounded-md leading-relaxed shadow-lg tracking-wider">
              ${rental?.price}/{rental?.time}
            </span>
          </div>
          <div className="w-full flex justify-end">
            <div
              className="w-10 h-10 bg-red-600 hover:bg-red-700 rounded-full flex items-center justify-center transition-all duration-300 transform hover:scale-105 shadow-lg cursor-pointer"
              onClick={() => {
                // navigate(`/rental-field-details/${rental?._id}`);
              }}
            >
              <ArrowUpRight className="w-6 h-6 text-white" />
            </div>
          </div>
        </div> */}

        {/* Price Badge - Always Visible */}
        <div className="absolute top-4 right-4 z-10">
          <div className="flex items-center w-full justify-between">
            <span className="px-3 py-1 uppe text-[#FF4141] bg-white text-xs sm:text-sm font-heading font-semibold rounded-md leading-relaxed shadow-lg tracking-wider">
              ${rental?.price}/{rental?.time}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return <RentalSkeleton />;
  }

  return (
    <div className="min-h-screen py-8 mb-5">
      <div className="container mx-auto px-4 md:px-8 lg:px-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <h1 className="font-heading text-2xl md:text-4xl font-bold tracking-wide text-gray-800">
              ALL FIELD RENTALS
            </h1>
          </div>
          <div className="text-sm text-gray-600">
            Showing {filteredRentals.length} of {rentals.length} fields
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search fields..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FF4141] focus:border-transparent outline-none"
              />
            </div>

            {/* Price Filter */}
            <select
              value={priceFilter}
              onChange={(e) => setPriceFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FF4141] focus:border-transparent outline-none"
            >
              <option value="all">All Prices</option>
              <option value="low">Low ($0 - $250)</option>
              <option value="medium">Medium ($251 - $350)</option>
              <option value="high">High ($350+)</option>
            </select>

            {/* Time Filter */}
            <select
              value={timeFilter}
              onChange={(e) => setTimeFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FF4141] focus:border-transparent outline-none"
            >
              <option value="all">All Time Units</option>
              <option value="per hour">Per Hour</option>
              <option value="per day">Per Day</option>
            </select>

            {/* Clear Filters */}
            <button
              onClick={() => {
                setSearchQuery("");
                setPriceFilter("all");
                setTimeFilter("all");
              }}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium rounded-md transition-all duration-300"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* Results Grid */}
        {filteredRentals.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredRentals.map((rental) => (
              <RentalCard key={rental._id} rental={rental} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="text-gray-400 text-6xl mb-4">🏟️</div>
            <h3 className="text-xl font-semibold text-gray-600 mb-2">
              No fields found
            </h3>
            <p className="text-gray-500">
              Try adjusting your search or filters
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AllRentalFields;
